import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosinstance";
import "./Forms.css";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import { useNavigate } from "react-router-dom";

export default function ClientManagementForm({ isSidebarExpanded }) {
  const [userData, setUserData] = useState({
    adminName: "",
    firm_id: "", // ✅ Correct field for firm selection
    email: "",
    phoneNumber: "",
  });

  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [technikPersons, setTechnikPersons] = useState([]); // ✅ Stores fetched firms

  useEffect(() => {
    allFirms();
  }, []);

  // ✅ Fetch firms from API
  const allFirms = async () => {
    try {
      const response = await axiosInstance.get("/api/get_all_firms");

      if (response.status === 200) {
        const firms = response.data.data;
        const firmOptions = firms.map((company) => ({
          label: company.firm_name,
          value: company.firm_id,
        }));

        setTechnikPersons(firmOptions);
      }
    } catch (error) {
      console.error("Error fetching firm data:", error);
    }
  };

  // ✅ Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setUserData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  // ✅ Validate Form
  const validateForm = () => {
    const newErrors = {};

    if (!userData.adminName.trim())
      newErrors.adminName = "Admin Name is required.";
    if (!userData.firm_id) newErrors.firm_id = "Firm Name is required.";

    if (!userData.email) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!userData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (!/^\d{10}$/.test(userData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits.";
    }

    setErrors(newErrors);
    console.log("Validation Errors:", newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle Save
  const handleSaveClick = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const response = await axiosInstance.post("/api/register_ca_admin", {
        Adminname: userData.adminName,
        Adminemail: userData.email,
        phone_number: userData.phoneNumber,
        firm_id: userData.firm_id,
      });

      if (response.status === 200) {
        setSnackbarMessage("Firm added successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        setTimeout(() => navigate("/ca-admin"), 2000);
      }
    } catch (error) {
      console.error("Error saving CA admin:", error);

      if (error.response?.status === 400) {
        setSnackbarMessage("Email is already registered");
      } else {
        setSnackbarMessage("Error saving CA Admin");
      }

      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setTimeout(() => setSnackbarOpen(false), 1000);
    }
  };

  return (
    <div
      className={`table-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body">
        <h1 className="form-title">Add Admins</h1>
        <div className="form-body">
          <form>
            <div className="form-group">
              <label className="form-label">
                Admin Name<span className="required">*</span>
              </label>
              <input
                type="text"
                name="adminName"
                className="input-field"
                placeholder="Enter Admin Name"
                value={userData.adminName}
                onChange={handleChange}
                autoFocus
              />
              {errors.adminName && <p className="error">{errors.adminName}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Firm Name<span className="required">*</span>
              </label>
              <select
                name="firm_id"
                className="input-field"
                value={userData.firm_id}
                onChange={handleChange}
              >
                <option value="">Select a Firm</option>
                {technikPersons.map((firm) => (
                  <option key={firm.value} value={firm.value}>
                    {firm.label}
                  </option>
                ))}
              </select>
              {errors.firm_id && <p className="error">{errors.firm_id}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Email<span className="required">*</span>
              </label>
              <input
                type="email"
                name="email"
                className="input-field"
                placeholder="Enter Email"
                value={userData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="error">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Phone<span className="required">*</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                className="input-field"
                placeholder="Enter Phone"
                value={userData.phoneNumber}
                onChange={handleChange}
              />
              {errors.phoneNumber && (
                <p className="error">{errors.phoneNumber}</p>
              )}
            </div>
          </form>
        </div>
      </div>
      <div className="button-group">
        <button
          type="button"
          className="btn btn-cancel"
          onClick={() => navigate(-1)}
        >
          CANCEL
        </button>
        <button
          type="submit"
          className="btn btn-save"
          onClick={handleSaveClick}
        >
          SAVE
        </button>
      </div>
       <Snackbar
              open={snackbarOpen}
              autoHideDuration={5000}
              onClose={() => setSnackbarOpen(false)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              style={{ transform: "translateY(40px)", marginLeft: "15px" }}
            >
              <MuiAlert
                onClose={() => setSnackbarOpen(false)}
                severity={snackbarSeverity}
                variant="standard"
              >
                {snackbarMessage}
              </MuiAlert>
            </Snackbar>
    </div>
  );
}
